import { useEffect, useRef, useState, useCallback } from "react";
import "./ServicesSection.css";

const API = process.env.REACT_APP_API_URL || "";

/* ══════════════════════════════════════════════════════════════════
   DATA
   ══════════════════════════════════════════════════════════════════ */
const SERVICES = [
    {
        id: "01",
        tag: "DESIGN & BUILD",
        name: "Drone Design",
        sub: "& Manufacturing",
        tagline: "From blank canvas to flight-ready airframe.",
        desc: "Custom UAV airframes engineered for agriculture, mapping, inspection, FPV, and enterprise operations. Every platform is designed around the mission — not the other way around.",
        mediaKey: "srv_design",
        accent: "#5d179e",
        rgb: "93,23,158",
        chips: ["Custom Airframes", "Payload Integration", "Performance Tuning", "Aerodynamic Optimization"],
    },
    {
        id: "02",
        tag: "ELECTRONICS",
        name: "Hardware",
        sub: "Development & Integration",
        tagline: "Flight-grade electronics, built in-house.",
        desc: "In-house PCB design, flight controller configuration, power systems, sensors, GPS, telemetry, and payload integration — all engineered for reliable UAV platforms.",
        mediaKey: "srv_hardware",
        accent: "#0ea5e9",
        rgb: "14,165,233",
        chips: ["PCB Design", "Flight Controllers", "Sensor Integration", "Power Systems"],
    },
    {
        id: "03",
        tag: "SOFTWARE",
        name: "Firmware",
        sub: "& Software Solutions",
        tagline: "Intelligent systems that power autonomous flight.",
        desc: "Custom flight firmware, ground control integration, mission planning tools, telemetry dashboards, and mobile app connectivity — the software layer that makes drones truly smart.",
        mediaKey: "srv_firmware",
        accent: "#10b981",
        rgb: "16,185,129",
        chips: ["Custom Firmware", "GCS Integration", "Mission Planning", "Mobile GCS"],
    },
    {
        id: "04",
        tag: "VALIDATION",
        name: "Testing",
        sub: "Calibration & Deployment",
        tagline: "Zero compromise on flight safety and reliability.",
        desc: "Complete validation workflow — calibration, flight testing, performance tuning, and client-ready deployment support. Every drone leaves our lab flight-proven.",
        mediaKey: "srv_testing",
        accent: "#f59e0b",
        rgb: "245,158,11",
        chips: ["Flight Testing", "System Calibration", "Safety Validation", "Pre-Deployment QA"],
    },
    {
        id: "05",
        tag: "CONSULTING",
        name: "Consulting",
        sub: "& Custom Projects",
        tagline: "End-to-end UAV solutions tailored to your mission.",
        desc: "Bespoke UAV solutions for startups, enterprises, agriculture firms, and research teams — including prototype development, system integration, and long-term R&D partnerships.",
        mediaKey: "srv_consulting",
        accent: "#ef4444",
        rgb: "239,68,68",
        chips: ["Prototype Development", "System Integration", "R&D Partnerships", "Enterprise Solutions"],
    },
];

/* ══════════════════════════════════════════════════════════════════
   FALLBACK SVGs (shown when no backend image is available)
   ══════════════════════════════════════════════════════════════════ */
function FallbackSVG({ accent, id }) {
    const map = {
        "01": (
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
                <rect width="400" height="300" fill="#0d0a18" />
                <rect x="168" y="128" width="64" height="36" rx="5"
                    fill={accent} opacity="0.12" stroke={accent} strokeWidth="1.4" />
                {[[168, 140, 60, 80], [232, 140, 340, 80], [168, 152, 60, 218], [232, 152, 340, 218]].map(([x1, y1, x2, y2], i) => (
                    <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={accent} strokeWidth="1.6" opacity="0.45" />
                ))}
                {[[60, 80], [340, 80], [60, 218], [340, 218]].map(([cx, cy], i) => (
                    <g key={i}>
                        <ellipse cx={cx} cy={cy} rx="28" ry="7" stroke={accent} strokeWidth="1" opacity="0.22"
                            transform={`rotate(${i % 2 === 0 ? -30 : 30} ${cx} ${cy})`} />
                        <circle cx={cx} cy={cy} r="11" fill={accent} opacity="0.65" />
                        <circle cx={cx} cy={cy} r="4" fill="#fff" opacity="0.5" />
                    </g>
                ))}
                <circle cx="200" cy="146" r="16" fill={accent} opacity="0.18" stroke={accent} strokeWidth="1.8" />
                <circle cx="200" cy="146" r="5" fill={accent} opacity="0.9" />
                <line x1="0" y1="146" x2="400" y2="146" stroke={accent} strokeWidth="0.4" opacity="0.1" strokeDasharray="4 8" />
                <line x1="200" y1="0" x2="200" y2="300" stroke={accent} strokeWidth="0.4" opacity="0.1" strokeDasharray="4 8" />
                <line x1="60" y1="274" x2="340" y2="274" stroke={accent} strokeWidth="0.9" opacity="0.22" />
                <line x1="60" y1="268" x2="60" y2="280" stroke={accent} strokeWidth="0.9" opacity="0.22" />
                <line x1="340" y1="268" x2="340" y2="280" stroke={accent} strokeWidth="0.9" opacity="0.22" />
                <text x="200" y="291" textAnchor="middle" fill={accent} fontSize="10" opacity="0.4" fontFamily="IBM Plex Mono,monospace">WHEELBASE · 460 mm</text>
            </svg>
        ),
        "02": (
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
                <rect width="400" height="300" fill="#0d0a18" />
                <rect x="100" y="44" width="200" height="164" rx="8" fill={accent} opacity="0.05" stroke={accent} strokeWidth="1.4" />
                <path d="M140 64 H174 V90 H210" stroke={accent} strokeWidth="1" opacity="0.25" fill="none" />
                <path d="M140 220 H164 V194 H210" stroke={accent} strokeWidth="1" opacity="0.25" fill="none" />
                <rect x="170" y="100" width="60" height="60" rx="4" fill={accent} opacity="0.14" stroke={accent} strokeWidth="1.6" />
                <text x="200" y="126" textAnchor="middle" fill={accent} fontSize="10" fontFamily="IBM Plex Mono,monospace">STM32</text>
                <text x="200" y="142" textAnchor="middle" fill={accent} fontSize="8.5" opacity="0.5" fontFamily="IBM Plex Mono,monospace">H7 · 480MHz</text>
                {[0, 1, 2, 3].map(i => (
                    <g key={i}>
                        <rect x={152} y={108 + i * 12} width={14} height={6} rx={2} fill={accent} opacity={0.45} />
                        <rect x={234} y={108 + i * 12} width={14} height={6} rx={2} fill={accent} opacity={0.45} />
                    </g>
                ))}
                <text x="200" y="256" textAnchor="middle" fill={accent} fontSize="8.5" opacity="0.3" fontFamily="IBM Plex Mono,monospace">AVIATRICKS FC · REV 1.2</text>
            </svg>
        ),
        "03": (
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
                <rect width="400" height="300" fill="#0d0a18" />
                <rect x="60" y="40" width="280" height="220" rx="10" fill={accent} opacity="0.04" stroke={accent} strokeWidth="1.2" />
                <rect x="60" y="40" width="280" height="30" rx="10" fill={accent} opacity="0.09" />
                <rect x="60" y="56" width="280" height="14" fill={accent} opacity="0.09" />
                {[76, 88, 100].map((x, i) => (
                    <circle key={i} cx={x} cy={55} r={5} fill={["#ef4444", "#f59e0b", "#10b981"][i]} opacity={0.75} />
                ))}
                <text x="200" y="60" textAnchor="middle" fill={accent} fontSize="9" opacity="0.3" fontFamily="IBM Plex Mono,monospace" letterSpacing="0.12em">DRONE COMMANDER GCS</text>
                {[["ARMED", "TRUE"], ["ALT", "42.3m"], ["SAT", "14/16"], ["BAT", "22.1V"], ["MODE", "AUTO"]].map(([l, v], i) => (
                    <g key={l}>
                        <text x="80" y={98 + i * 26} fill={accent} fontSize="9" opacity="0.35" fontFamily="IBM Plex Mono,monospace">{l}</text>
                        <text x="200" y={98 + i * 26} fill={accent} fontSize="9" opacity="0.85" fontFamily="IBM Plex Mono,monospace">{v}</text>
                    </g>
                ))}
                <rect x="240" y="78" width="88" height="158" rx="4" fill={accent} opacity="0.04" stroke={accent} strokeWidth="0.8" />
                <path d="M248 200 Q270 160 284 148 Q298 136 318 118" stroke={accent} strokeWidth="1.4" opacity="0.55" fill="none" />
                <circle cx="284" cy="148" r="5" fill={accent} opacity="0.85" />
                <circle cx="284" cy="148" r="12" stroke={accent} strokeWidth="0.7" opacity="0.3" />
            </svg>
        ),
        "04": (
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
                <rect width="400" height="300" fill="#0d0a18" />
                <line x1="48" y1="248" x2="378" y2="248" stroke={accent} strokeWidth="0.9" opacity="0.28" />
                <line x1="48" y1="40" x2="48" y2="248" stroke={accent} strokeWidth="0.9" opacity="0.28" />
                {[1, 2, 3, 4].map(i => (
                    <line key={i} x1={48} y1={248 - i * 52} x2={378} y2={248 - i * 52} stroke={accent} strokeWidth="0.5" opacity="0.1" strokeDasharray="4 8" />
                ))}
                <path d="M48 246 L90 214 L130 178 L164 140 L196 112 L228 116 L262 120 L298 106 L340 90 L372 76"
                    stroke={accent} strokeWidth="2.5" opacity="0.8" fill="none" />
                <path d="M48 246 L90 214 L130 178 L164 140 L196 112 L228 116 L262 120 L298 106 L340 90 L372 76 L372 248Z"
                    fill={accent} opacity="0.06" />
                <circle cx="372" cy="76" r="6" fill={accent} opacity="0.9" />
                <circle cx="372" cy="76" r="14" stroke={accent} strokeWidth="1" opacity="0.22" />
                {[["HOVER STABILITY", "✓", 0], ["GPS ACCURACY", "✓", 1], ["RTH SEQUENCE", "✓", 2], ["PAYLOAD OK", "✓", 3]].map(([t, c, i]) => (
                    <g key={t}>
                        <text x="68" y={60 + i * 22} fill="#10b981" fontSize="10" opacity="0.8" fontFamily="IBM Plex Mono,monospace">{c}</text>
                        <text x="84" y={60 + i * 22} fill={accent} fontSize="9.5" opacity="0.55" fontFamily="IBM Plex Mono,monospace">{t}</text>
                    </g>
                ))}
                <text x="210" y="275" textAnchor="middle" fill={accent} fontSize="9" opacity="0.28" fontFamily="IBM Plex Mono,monospace">FLIGHT TIME (s)</text>
            </svg>
        ),
        "05": (
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}>
                <rect width="400" height="300" fill="#0d0a18" />
                {["Agriculture", "Mapping", "Inspection", "Enterprise", "FPV"].map((s, i) => {
                    const a = (i / 5) * 2 * Math.PI - Math.PI / 2;
                    const x = 200 + 106 * Math.cos(a), y = 150 + 88 * Math.sin(a);
                    return (
                        <g key={s}>
                            <line x1={200} y1={150} x2={x} y2={y} stroke={accent} strokeWidth="0.9" opacity="0.2" strokeDasharray="3 6" />
                            <circle cx={x} cy={y} r="30" fill={accent} opacity="0.07" stroke={accent} strokeWidth="0.9" />
                            <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fill={accent} fontSize="10" opacity="0.72" fontFamily="IBM Plex Mono,monospace">{s}</text>
                        </g>
                    );
                })}
                <circle cx="200" cy="150" r="30" fill={accent} opacity="0.12" stroke={accent} strokeWidth="1.5" />
                <text x="200" y="151" textAnchor="middle" dominantBaseline="middle" fill={accent} fontSize="10" opacity="0.9" fontFamily="Bebas Neue,sans-serif" letterSpacing="2">AVIATRICKS</text>
                <circle cx="200" cy="150" r="52" stroke={accent} strokeWidth="0.4" opacity="0.07" />
                <circle cx="200" cy="150" r="75" stroke={accent} strokeWidth="0.3" opacity="0.05" />
            </svg>
        ),
    };
    return map[id] ?? map["01"];
}

/* ══════════════════════════════════════════════════════════════════
   HOOK — fires callback once when element enters viewport
   ══════════════════════════════════════════════════════════════════ */
function useOnceVisible(ref, callback, options = {}) {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                callback();
                obs.disconnect();
            }
        }, { threshold: 0.1, ...options });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
}

/* ══════════════════════════════════════════════════════════════════
   SERVICE CARD
   ══════════════════════════════════════════════════════════════════ */
function ServiceCard({ svc, index, imageUrl }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    const onVisible = useCallback(() => {
        setTimeout(() => setVisible(true), index * 100);
    }, [index]);

    useOnceVisible(ref, onVisible, { threshold: 0.08 });

    const imgSrc = imageUrl
        ? (imageUrl.startsWith("http") ? imageUrl : `${API}${imageUrl}`)
        : null;

    return (
        <article
            ref={ref}
            className={`srv-card${visible ? " srv-card--visible" : ""}`}
            style={{ "--a": svc.accent, "--rgb": svc.rgb }}
        >
            {/* Ghost number stamp */}
            <div className="srv-stamp" aria-hidden="true">{svc.id}</div>

            {/* ── Image panel ── */}
            <div className="srv-img-wrap">
                <div
                    className="srv-img-bg"
                    style={imgSrc ? { backgroundImage: `url(${imgSrc})` } : {}}
                >
                    {!imgSrc && <FallbackSVG accent={svc.accent} id={svc.id} />}
                </div>

                {/* gradient overlay */}
                <div
                    className="srv-img-overlay"
                    style={{
                        background: `linear-gradient(to bottom,
              rgba(${svc.rgb},0.18) 0%,
              rgba(13,10,24,0.70) 100%)`,
                    }}
                />

                {/* wipe-reveal curtain is :after in CSS */}

                {/* floating tag */}
                <span
                    className="srv-tag"
                    style={{
                        color: svc.accent,
                        borderColor: `rgba(${svc.rgb},0.38)`,
                    }}
                >
                    {svc.tag}
                </span>
            </div>

            {/* Sliding accent rule */}
            <div className="srv-rule" style={{ background: svc.accent }} />

            {/* ── Text body ── */}
            <div className="srv-body">
                <h3 className="srv-name">
                    {svc.name}
                    <span className="srv-name-sub">{svc.sub}</span>
                </h3>

                <p className="srv-tagline" style={{ color: svc.accent }}>
                    {svc.tagline}
                </p>

                <p className="srv-desc">{svc.desc}</p>

                <div className="srv-chips">
                    {svc.chips.map((c, i) => (
                        <span
                            key={c}
                            className="srv-chip"
                            style={{
                                borderColor: `rgba(${svc.rgb},0.22)`,
                                // stagger chips after card is visible
                                transitionDelay: visible ? `${0.55 + i * 0.07}s` : "0s",
                            }}
                        >
                            {c}
                        </span>
                    ))}
                </div>
            </div>

            {/* Ambient hover glow */}
            <div
                className="srv-glow"
                style={{
                    background: `radial-gradient(ellipse at 50% 0%,
            rgba(${svc.rgb},0.1) 0%, transparent 70%)`,
                }}
            />
        </article>
    );
}

/* ══════════════════════════════════════════════════════════════════
   SECTION ROOT
   ══════════════════════════════════════════════════════════════════ */
export default function ServicesSection() {
    const [urls, setUrls] = useState({});

    // header entrance
    const headerRef = useRef(null);
    const [headerVisible, setHeaderVisible] = useState(false);
    useOnceVisible(headerRef, () => setHeaderVisible(true), { threshold: 0.15 });

    // footer entrance
    const footerRef = useRef(null);
    const [footerVisible, setFooterVisible] = useState(false);
    useOnceVisible(footerRef, () => setFooterVisible(true), { threshold: 0.2 });

    // fetch images from backend — same pattern as FlyhubHero
    useEffect(() => {
        fetch(`${API}/api/media/services`)
            .then(r => r.json())
            .then(d => setUrls(d || {}))
            .catch(() => setUrls({}));
    }, []);

    return (
        <section className="srv-wrap" aria-label="Our services">
            <div className="srv-bg-grid" aria-hidden="true" />

            <div className="srv-inner">

                {/* ── HEADER ── */}
                <header
                    ref={headerRef}
                    className={`srv-header${headerVisible ? " srv-header--visible" : ""}`}
                >
                    <p className="srv-eyebrow">
                        <span className="srv-dot" aria-hidden="true" />
                        What We Do
                    </p>

                    <h2 className="srv-h2">
                        Five Disciplines.<br />
                        <em>One Pipeline.</em>
                    </h2>

                    <p className="srv-sub">
                        Every service we offer is part of a unified pipeline — from the
                        first sketch of an airframe to the final field deployment. No
                        handoffs to third parties. No gaps in the stack.
                    </p>
                </header>

                {/* ── CARDS ── */}
                <div className="srv-grid">
                    {SERVICES.map((svc, i) => (
                        <ServiceCard
                            key={svc.id}
                            svc={svc}
                            index={i}
                            imageUrl={urls?.[svc.mediaKey] ?? null}
                        />
                    ))}
                </div>

                {/* ── FOOTER ── */}
                <footer
                    ref={footerRef}
                    className={`srv-footer${footerVisible ? " srv-footer--visible" : ""}`}
                >
                    <p className="srv-footer-note">
                        All services are delivered end-to-end from our lab in Namakkal,
                        Tamil Nadu — with no outsourced dependencies.
                    </p>
                    <div className="srv-footer-loc">
                        <span className="srv-footer-dot" aria-hidden="true" />
                        Namakkal · Tamil Nadu · India
                    </div>
                </footer>

            </div>
        </section>
    );
}